/**
 * Hook for initializing auth session on app load.
 * Includes inactivity timeout: if more than INACTIVITY_TIMEOUT_MS has passed
 * since the last user activity, the session is invalidated on reload.
 * Also checks MFA status — if MFA is required but not configured, forces logout.
 */
import { useEffect, useState, useCallback } from 'react';
import { useAuthStore } from '../store';
import { getCurrentSession, getTokensFromSession, getUserInfoFromSession, logout as cognitoLogout } from '../services/cognito-auth';
import { authApi } from '../services/api-client';
const ACTIVITY_KEY = 'ipsoft-last-activity';
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
/**
 * Update the last activity timestamp in localStorage.
 */
function updateActivity() {
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
}
/**
 * Check if the session has expired due to inactivity.
 */
function isSessionExpiredByInactivity() {
    const lastActivity = localStorage.getItem(ACTIVITY_KEY);
    if (!lastActivity)
        return false; // First visit, no timeout
    const elapsed = Date.now() - parseInt(lastActivity, 10);
    return elapsed > INACTIVITY_TIMEOUT_MS;
}
/**
 * Returns `initializing: true` while checking for an existing session.
 * If more than 15 minutes have passed since last activity, forces logout.
 * If MFA is required but not configured, forces logout on F5.
 */
export function useSessionInit() {
    const [initializing, setInitializing] = useState(true);
    const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
    const setMfaConfigured = useAuthStore((s) => s.setMfaConfigured);
    const storeLogout = useAuthStore((s) => s.logout);
    useEffect(() => {
        // Check inactivity before restoring session
        if (isSessionExpiredByInactivity()) {
            cognitoLogout();
            storeLogout();
            localStorage.removeItem(ACTIVITY_KEY);
            setInitializing(false);
            return;
        }
        getCurrentSession()
            .then(async (session) => {
            if (session) {
                const tokens = getTokensFromSession(session);
                const userInfo = getUserInfoFromSession(session);
                setAuthenticated(tokens, userInfo);
                updateActivity();
                // Check MFA status — if required but not configured, force logout
                try {
                    const { data } = await authApi.get('/auth/mfa-setup/status');
                    if (data.mfaRequired) {
                        // MFA not configured — logout and force re-login (will redirect to /mfa-setup)
                        cognitoLogout();
                        storeLogout();
                        localStorage.removeItem(ACTIVITY_KEY);
                    }
                    else {
                        setMfaConfigured(true);
                    }
                }
                catch {
                    // If status check fails, allow access (don't break on network error)
                    setMfaConfigured(true);
                }
            }
        })
            .finally(() => setInitializing(false));
    }, [setAuthenticated, setMfaConfigured, storeLogout]);
    // Track user activity (throttled)
    const handleActivity = useCallback(() => {
        updateActivity();
    }, []);
    useEffect(() => {
        const events = ['click', 'keydown', 'touchstart'];
        let lastUpdate = 0;
        const throttledUpdate = () => {
            const now = Date.now();
            if (now - lastUpdate > 60000) {
                lastUpdate = now;
                handleActivity();
            }
        };
        events.forEach(event => window.addEventListener(event, throttledUpdate, { passive: true }));
        return () => {
            events.forEach(event => window.removeEventListener(event, throttledUpdate));
        };
    }, [handleActivity]);
    return { initializing };
}
//# sourceMappingURL=useSessionInit.js.map